import { useEffect } from "react";
import { DocumentViewModel } from "../ViewModels";
import { useDocumentEnricher, useExportDocument } from ".";
import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_TYPES,
    OPERATORS,
    CASES,
    UIAction,
    ActionResponse,
    useDetailLifeCycle,
} from "@/Shared";

import { useDocument } from "@/OneLiners";
import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { DocumentModel } from "../DocumentModel";


/**
 * useDocumentDetail hook
 *
 * Domain-specific detail hook for Document entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - UI feedback mechanisms (toast notifications, confirmation dialogs),
 * - document download/export operations,
 * - and navigation concerns.
 *
 * Responsibilities:
 * - Initialize the DocumentViewModel when data is available.
 * - Handle delete operations with access control, confirmation modal, and toast feedback.
 * - Handle PDF export through a delegated hook.
 * - Manage navigation (form, detail view, layout) based on user actions and permissions.
 * - Expose entity VM, action executor, access rights, and navigation methods to the UI layer.
 *
 * Author: Samano CASTRE
 * Date: March 2, 2026
 *
 * @see DomainHookProps
 * @see DomainDetailHook<DocumentViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<DocumentViewModel>
 */
export function useDocumentDetail(props: DomainHookProps): DomainDetailHook<DocumentViewModel> {
    const { permissionHook, accessRight, uiFeedbackHooks } = props;
    const { domainType, domainLabel } = DOMAIN_CONFIGS.Document;

    /**
     * Document domain CRUD operations and state
     */
    const documentHook = useDocument();

    /**
     * Document-related enums exposed to the UI layer
     */
    const { DocumentEnums } = useAppEnum();

    /**
     * Document domain data and operations
     * Provided by the document enricher facade
     */
    const { enricherResponse, getStrictEnrichedDocument } = useDocumentEnricher();


    /**
     * Combined response from document enrichment and media files fetching
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            documentHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: documentHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Use the shared export hook for the Document domain
     */
    const { exportDocumentResponse, executeExportPDF } = useExportDocument(
        DOMAIN_TYPES.Document, 
        uiFeedbackHooks,
        documentHook.generateAction,
        documentHook.exportAction
    );

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: documentHook,
        buildVM: (currentEntity: DocumentModel, isDisplaying: boolean) => {
            return new DocumentViewModel({
                document: getStrictEnrichedDocument(currentEntity),
                viewEnums: DocumentEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    const { requestId } = lifeCycleHook;

    /**
     * Executes a domain action requested by the UI layer.
     * Acts as the single entry point for all Document-related actions.
     */
    const computeUIActions = (): UIAction[] => {
        const actions: UIAction[] = [];
        if (!requestId) {
            return actions;
        }

        const canExport = accessRight.canExport(domainType);
        actions.push({
            key: `${domainType}-document-${requestId}`,
            label: "Exporter",
            onClick: () => canExport
                ? executeExportPDF('download', {
                    filters: {
                        id: {
                            value: requestId,
                            operator: OPERATORS.EQUALS,
                            case: CASES.SENSITIVE,
                            label: 'id'
                        }
                    }
                })
                : undefined,

            placement: "sub",
            type: 'document',
            icon: 'FaFilePdf',
            disabled: !canExport,
            loading: ActionStatusHelper.isLoading(exportDocumentResponse.status),
            tooltip: canExport
                ? `Cliquez pour exporter "${domainLabel}".`
                : `Vous n'avez pas les droits suffisants pour exporter "${domainLabel}".`
        });

        return actions;
    };

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook,
        computeUIActions,
    }
}